import React, { useRef, useEffect, useState, useCallback } from 'react';
import micIcon from '../../styles/assets/img/chat/mic.svg';
import audioWaveIcon from '../../styles/assets/img/chat/audio-wave.svg';
import sendMask from '../../styles/assets/img/chat/send-mask.png';
import ChatIcon from './ChatIcon';
import { toPlainText } from './chatConfig';

const sendMaskStyle = {
    maskImage: `url(${sendMask})`,
    WebkitMaskImage: `url(${sendMask})`,
};

/**
 * Auto-growing textarea with a send button and voice-to-text microphone.
 * Enter sends, Shift+Enter inserts a newline.
 * Mic button uses the Web Speech API for voice-to-text, with a MediaRecorder
 * fallback that uploads audio to the backend for server-side transcription
 * when the Web Speech API is unavailable (e.g. corporate firewall blocks Google).
 * Set startListeningOnMount to begin voice input as soon as the input appears.
 */
export default function ChatInput({ value, onChange, onSend, disabled, placeholder, startListeningOnMount = false, voiceReply }) {
    const textareaRef = useRef(null);
    const recognitionRef = useRef(null);
    const mediaRecorderRef = useRef(null);
    const audioChunksRef = useRef([]);
    const mediaStreamRef = useRef(null);
    const [isListening, setIsListening] = useState(false);
    const [isTranscribing, setIsTranscribing] = useState(false);
    const [speechSupported, setSpeechSupported] = useState(true);
    const [isContinuous, setIsContinuous] = useState(false);
    const [isSpeaking, setIsSpeaking] = useState(false);
    const waitingReplyRef = useRef(false);
    const audioRef = useRef(null);
    const audioUrlRef = useRef(null);
    const speechRequestRef = useRef(null);
    const mountedRef = useRef(true);
    const sessionRef = useRef(0);
    const latestRef = useRef({ value, onChange, onSend, disabled });
    latestRef.current = { value, onChange, onSend, disabled };
    const startRecordingRef = useRef(null);
    const [speechStatus, setSpeechStatus] = useState(''); // user-visible status text
    const [useFallbackMode, setUseFallbackMode] = useState(true); // use MediaRecorder + Whisper server by default
    const finalTranscriptRef = useRef('');
    const baseTextRef = useRef('');
    const shouldRestartRef = useRef(false);
    const hadErrorRef = useRef(false);
    const continuousModeRef = useRef(false);
    const silenceTimerRef = useRef(null); // timeout ref for silence detection
    const lastSpeechTimeRef = useRef(0); // last time we detected speech
    const isStoppingRef = useRef(false); // guard to prevent double-stop
    const manualStopRef = useRef(false); // true when user manually clicked mic to stop
    const SILENCE_THRESHOLD = 0.04; // RMS volume below this = silence (raised to ignore background noise)
    const SILENCE_DURATION_MS = 2000; // silence duration before auto-submit
    const MIN_SPEECH_VOLUME = 0.03; // minimum max volume for recording to be considered actual speech

    // Check if the Web Speech API is supported
    useEffect(() => {
        setSpeechSupported(!!navigator.mediaDevices?.getUserMedia && !!window.MediaRecorder);
    }, []);

    // Auto-resize the textarea based on content
    useEffect(() => {
        const ta = textareaRef.current;
        if (!ta) return;
        ta.style.height = 'auto';
        ta.style.height = Math.min(ta.scrollHeight, 100) + 'px';
    }, [value]);

    // Cleanup speech recognition and media recorder on unmount
    useEffect(() => {
        mountedRef.current = true;
        return () => {
            mountedRef.current = false;
            sessionRef.current += 1;
            speechRequestRef.current?.abort();
            audioRef.current?.pause();
            if (audioUrlRef.current) URL.revokeObjectURL(audioUrlRef.current);
            shouldRestartRef.current = false;
            continuousModeRef.current = false;
            if (silenceTimerRef.current) {
                clearTimeout(silenceTimerRef.current);
                silenceTimerRef.current = null;
            }
            if (recognitionRef.current) {
                try { recognitionRef.current.abort(); } catch (_) { /* noop */ }
            }
            if (volumeTimerRef.current) {
                clearInterval(volumeTimerRef.current);
                volumeTimerRef.current = null;
            }
            if (audioContextRef.current) {
                try { audioContextRef.current.close(); } catch (_) { /* noop */ }
                audioContextRef.current = null;
            }
            if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
                mediaRecorderRef.current.onstop = null;
                try { mediaRecorderRef.current.stop(); } catch (_) { /* noop */ }
            }
            if (mediaStreamRef.current) {
                mediaStreamRef.current.getTracks().forEach(t => t.stop());
            }
        };
    }, []);

    // Clear speech status after a few seconds when not listening
    useEffect(() => {
        if (speechStatus && !isListening) {
            const timer = setTimeout(() => setSpeechStatus(''), 4000);
            return () => clearTimeout(timer);
        }
    }, [speechStatus, isListening]);

    const handleKeyDown = (e) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            handleSend();
        }
    };

    const handleSend = () => {
        if (disabled || isListening || isTranscribing || isSpeaking || isContinuous || !value.trim()) return;
        onSend();
    };

    const handleMicToggle = useCallback(() => {
        if (disabled || isTranscribing || continuousModeRef.current) return;

        // If already listening, stop
        if (isListening) {
            manualStopRef.current = true; // user manually stopped — don't auto-restart
            if (useFallbackMode) {
                if (!mediaRecorderRef.current || mediaRecorderRef.current.state === 'inactive') {
                    sessionRef.current += 1;
                    setIsListening(false);
                    setSpeechStatus('');
                    return;
                }
                stopFallbackRecording();
            } else {
                shouldRestartRef.current = false;
                if (recognitionRef.current) {
                    try { recognitionRef.current.stop(); } catch (_) { /* noop */ }
                }
                setIsListening(false);
                setSpeechStatus('');
            }
            return;
        }

        // Always use MediaRecorder + Whisper server for transcription
        continuousModeRef.current = false;
        startFallbackRecording();
    }, [disabled, isListening, isTranscribing, useFallbackMode, value, onChange]);

    const startRecognition = useCallback((SpeechRecognition) => {
        const recognition = new SpeechRecognition();
        recognition.continuous = true;
        recognition.interimResults = true;
        recognition.lang = 'en-US';

        finalTranscriptRef.current = '';
        baseTextRef.current = value ? value.trimEnd() : '';
        shouldRestartRef.current = true;
        hadErrorRef.current = false;

        recognition.onstart = () => {
            setIsListening(true);
            setSpeechStatus('🎤 Listening… speak now');
        };

        recognition.onresult = (event) => {
            let interim = '';
            for (let i = event.resultIndex; i < event.results.length; i++) {
                const transcript = event.results[i][0].transcript;
                if (event.results[i].isFinal) {
                    finalTranscriptRef.current += transcript;
                } else {
                    interim += transcript;
                }
            }
            // Combine existing text with recognized speech
            const base = baseTextRef.current;
            const needsSpace = base.length > 0 && !base.endsWith(' ');
            const prefix = needsSpace ? base + ' ' : base;
            const newText = prefix + finalTranscriptRef.current + interim;
            onChange(newText);

            // Auto-scroll textarea to bottom
            if (textareaRef.current) {
                textareaRef.current.scrollTop = textareaRef.current.scrollHeight;
            }
        };

        recognition.onerror = (event) => {
            console.warn('Speech recognition error:', event.error);
            hadErrorRef.current = true;

            if (event.error === 'no-speech') {
                // No speech detected — don't show scary error, just let onend restart
                hadErrorRef.current = false;
                return;
            }
            if (event.error === 'aborted') {
                hadErrorRef.current = false;
                return;
            }
            if (event.error === 'not-allowed' || event.error === 'service-not-allowed') {
                shouldRestartRef.current = false;
                setIsListening(false);
                setSpeechStatus('🎤 Microphone access denied. Please allow mic access in your browser settings.');
                return;
            }
            if (event.error === 'network') {
                // Chrome's Web Speech API needs internet to reach Google's servers.
                // Switch to fallback MediaRecorder mode for server-side transcription.
                shouldRestartRef.current = false;
                setIsListening(false);
                setUseFallbackMode(true);
                setSpeechStatus('🎤 Switching to server-side transcription…');
                // Automatically start fallback recording
                setTimeout(() => startFallbackRecording(), 500);
                return;
            }
            // For other errors, show a generic message
            shouldRestartRef.current = false;
            setIsListening(false);
            setSpeechStatus(`🎤 Voice error: ${event.error}. Try again.`);
        };

        recognition.onend = () => {
            // Chrome stops recognition after silence — auto-restart if we're still in listening mode
            if (shouldRestartRef.current) {
                try {
                    recognition.start();
                } catch (_) {
                    setIsListening(false);
                    if (!hadErrorRef.current) setSpeechStatus('');
                }
            } else {
                setIsListening(false);
                // Don't clear the error message — let the auto-clear timeout handle it
                if (!hadErrorRef.current) setSpeechStatus('');
            }
        };

        recognitionRef.current = recognition;
        try {
            recognition.start();
        } catch (err) {
            console.warn('Failed to start recognition:', err);
            setIsListening(false);
            setSpeechStatus('🎤 Could not start voice input. Try again.');
        }

        // Focus the textarea so the user sees the text appearing
        if (textareaRef.current) {
            textareaRef.current.focus();
        }
    }, [value, onChange]);

    // ── Fallback: MediaRecorder + server-side transcription ──

    // Real-time audio level monitoring refs
    const audioContextRef = useRef(null);
    const analyserRef = useRef(null);
    const volumeTimerRef = useRef(null);
    const maxVolumeRef = useRef(0);

    const startFallbackRecording = useCallback(() => {
        const session = sessionRef.current;
        if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
            setSpeechStatus('🎤 Voice input not supported in this browser.');
            return;
        }

        // Request audio with aggressive noise suppression constraints
        const audioConstraints = {
            echoCancellation: true,
            noiseSuppression: true,
            autoGainControl: false, // disable AGC — it can amplify background noise
            channelCount: 1,
            sampleRate: 16000,
        };

        setIsListening(true);
        navigator.mediaDevices.getUserMedia({ audio: audioConstraints })
            .then((stream) => {
                if (!mountedRef.current || session !== sessionRef.current) {
                    stream.getTracks().forEach(track => track.stop());
                    return;
                }
                mediaStreamRef.current = stream;
                audioChunksRef.current = [];
                maxVolumeRef.current = 0;

                // Set up real-time volume monitoring
                try {
                    const audioCtx = new (window.AudioContext || window.webkitAudioContext)();
                    const source = audioCtx.createMediaStreamSource(stream);
                    const analyser = audioCtx.createAnalyser();
                    analyser.fftSize = 256;
                    source.connect(analyser);
                    audioContextRef.current = audioCtx;
                    analyserRef.current = analyser;

                    const dataArray = new Uint8Array(analyser.frequencyBinCount);
                    lastSpeechTimeRef.current = Date.now();
                    volumeTimerRef.current = setInterval(() => {
                        analyser.getByteTimeDomainData(dataArray);
                        // Calculate RMS volume
                        let sum = 0;
                        for (let i = 0; i < dataArray.length; i++) {
                            const v = (dataArray[i] - 128) / 128;
                            sum += v * v;
                        }
                        const rms = Math.sqrt(sum / dataArray.length);
                        const pct = Math.round(rms * 100);
                        if (rms > maxVolumeRef.current) {
                            maxVolumeRef.current = rms;
                        }

                        // Track speech vs silence for continuous mode
                        if (rms > SILENCE_THRESHOLD) {
                            lastSpeechTimeRef.current = Date.now();
                            // Clear any pending silence timer
                            if (silenceTimerRef.current) {
                                clearTimeout(silenceTimerRef.current);
                                silenceTimerRef.current = null;
                            }
                        } else if (!isStoppingRef.current) {
                            // In continuous mode — check if we've had enough speech followed by silence
                            const silenceMs = Date.now() - lastSpeechTimeRef.current;
                            if (silenceMs >= SILENCE_DURATION_MS && maxVolumeRef.current > SILENCE_THRESHOLD) {
                                // We had speech, then silence — auto-submit
                                isStoppingRef.current = true;
                                if (silenceTimerRef.current) {
                                    clearTimeout(silenceTimerRef.current);
                                    silenceTimerRef.current = null;
                                }
                                setSpeechStatus('Transcribing...');
                                stopFallbackRecording();
                                return;
                            }
                        }

                        // Update status with volume indicator
                        const bars = Math.min(10, Math.floor(pct / 5));
                        const bar = '█'.repeat(bars) + '░'.repeat(10 - bars);
                        if (continuousModeRef.current && !isStoppingRef.current) {
                            const silenceMs = Date.now() - lastSpeechTimeRef.current;
                            const silenceIndicator = silenceMs > 500 ? ` (silence: ${Math.round(silenceMs/1000)}s)` : '';
                            setSpeechStatus(`🎤 Listening… ${bar} ${pct}%${silenceIndicator}`);
                        } else {
                            setSpeechStatus(`🎤 Listening… ${bar} ${pct}%`);
                        }
                    }, 100);
                } catch (e) {
                    console.warn('Volume monitoring failed:', e);
                }

                // Pick the best supported audio format
                const mimeType = MediaRecorder.isTypeSupported('audio/webm;codecs=opus')
                    ? 'audio/webm;codecs=opus'
                    : MediaRecorder.isTypeSupported('audio/webm')
                        ? 'audio/webm'
                        : MediaRecorder.isTypeSupported('audio/mp4')
                            ? 'audio/mp4'
                            : '';

                const recorder = mimeType
                    ? new MediaRecorder(stream, { mimeType })
                    : new MediaRecorder(stream);

                recorder.ondataavailable = (e) => {
                    if (e.data.size > 0) {
                        audioChunksRef.current.push(e.data);
                    }
                };

                recorder.onstop = async () => {
                    if (!mountedRef.current || session !== sessionRef.current) return;
                    // Stop volume monitoring
                    if (volumeTimerRef.current) {
                        clearInterval(volumeTimerRef.current);
                        volumeTimerRef.current = null;
                    }
                    if (audioContextRef.current) {
                        try { audioContextRef.current.close(); } catch (_) { /* noop */ }
                        audioContextRef.current = null;
                    }

                    // Stop the mic stream
                    if (mediaStreamRef.current) {
                        mediaStreamRef.current.getTracks().forEach(t => t.stop());
                        mediaStreamRef.current = null;
                    }

                    setIsListening(false);
                    const totalSize = audioChunksRef.current.reduce((s, c) => s + c.size, 0);
                    const maxVol = maxVolumeRef.current;

                    if (totalSize < 200) {
                        setIsListening(false);
                        setSpeechStatus('🎤 No audio captured. Check your microphone and try again.');
                        continuousModeRef.current = false;
                        setIsContinuous(false);
                        return;
                    }

                    // Warn if the recording was essentially silent or just background noise
                    if (maxVol < MIN_SPEECH_VOLUME) {
                        setIsListening(false);
                        setSpeechStatus('🎤 No clear speech detected — only background noise. Try speaking closer to the mic.');
                        continuousModeRef.current = false;
                        setIsContinuous(false);
                        return;
                    }

                    // Create audio blob and upload for transcription
                    const blob = new Blob(audioChunksRef.current, {
                        type: mimeType || 'audio/webm'
                    });
                    setIsTranscribing(true);
                    setSpeechStatus('🎤 Transcribing your speech…');

                    try {
                        const formData = new FormData();
                        const ext = mimeType.includes('webm') ? 'webm' : mimeType.includes('mp4') ? 'mp4' : 'audio';
                        formData.append('file', blob, `recording.${ext}`);

                        const apiUrl = process.env.REACT_APP_API_URL || '/api/';
                        const controller = new AbortController();
                        speechRequestRef.current = controller;
                        const response = await fetch(`${apiUrl}Chat/transcribe`, {
                            method: 'POST',
                            body: formData,
                            credentials: 'include',
                            signal: controller.signal,
                        });

                        if (!response.ok) {
                            const errData = await response.json().catch(() => ({}));
                            throw new Error(errData.error || `Server returned ${response.status}`);
                        }

                        const data = await response.json();
                        if (!mountedRef.current || session !== sessionRef.current) return;
                        const transcript = data.text || '';
                        const wasManual = manualStopRef.current;

                        if (transcript.trim()) {
                            // Append transcript to existing text
                            const base = latestRef.current.value ? latestRef.current.value.trimEnd() : '';
                            const needsSpace = base.length > 0 && !base.endsWith(' ');
                            const newText = needsSpace ? `${base} ${transcript}` : `${base}${transcript}`;
                            latestRef.current.onChange(newText);
                            if (continuousModeRef.current && !wasManual) {
                                waitingReplyRef.current = true;
                                setSpeechStatus('Waiting for reply...');
                                latestRef.current.onSend(newText);
                            } else {
                                setSpeechStatus('Ready');
                                textareaRef.current?.focus();
                            }
                        } else if (continuousModeRef.current && !wasManual) {
                            startRecordingRef.current();
                        }

                        if (wasManual) {
                            setSpeechStatus('');
                        }
                    } catch (err) {
                        if (!mountedRef.current || session !== sessionRef.current || err.name === 'AbortError') return;
                        console.warn('Transcription failed:', err);
                        setSpeechStatus(`🎤 Transcription failed: ${err.message}`);
                        isStoppingRef.current = false;
                        continuousModeRef.current = false;
                        setIsContinuous(false);
                    } finally {
                        if (!mountedRef.current || session !== sessionRef.current) return;
                        setIsTranscribing(false);
                        if (manualStopRef.current) {
                            setIsListening(false);
                        }
                    }
                };

                mediaRecorderRef.current = recorder;
                recorder.start(1000); // Collect data in 1-second chunks
                isStoppingRef.current = false;
                manualStopRef.current = false;
                lastSpeechTimeRef.current = Date.now();
                setIsListening(true);
                setSpeechStatus('Listening...');

                // Focus the textarea
                if (textareaRef.current) {
                    textareaRef.current.focus();
                }
            })
            .catch((err) => {
                if (!mountedRef.current || session !== sessionRef.current) return;
                console.warn('Mic permission denied:', err);
                setIsListening(false);
                setSpeechStatus('🎤 Microphone access denied. Please allow mic access in your browser.');
                continuousModeRef.current = false;
                setIsContinuous(false);
            });
    }, [value, onChange, onSend]);
    startRecordingRef.current = startFallbackRecording;

    const stopFallbackRecording = useCallback(() => {
        if (silenceTimerRef.current) {
            clearTimeout(silenceTimerRef.current);
            silenceTimerRef.current = null;
        }
        if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
            // Request any remaining buffered data before stopping
            try { mediaRecorderRef.current.requestData(); } catch (_) { /* noop */ }
            mediaRecorderRef.current.stop();
        }
        if (manualStopRef.current) {
            setIsListening(false);
        }
    }, []);

    const stopConversation = useCallback(() => {
        sessionRef.current += 1;
        continuousModeRef.current = false;
        manualStopRef.current = true;
        waitingReplyRef.current = false;
        speechRequestRef.current?.abort();
        audioRef.current?.pause();
        if (audioUrlRef.current) URL.revokeObjectURL(audioUrlRef.current);
        audioUrlRef.current = null;
        audioRef.current = null;
        if (mediaRecorderRef.current) mediaRecorderRef.current.onstop = null;
        stopFallbackRecording();
        clearInterval(volumeTimerRef.current);
        volumeTimerRef.current = null;
        audioContextRef.current?.close().catch(() => {});
        audioContextRef.current = null;
        mediaStreamRef.current?.getTracks().forEach(track => track.stop());
        mediaStreamRef.current = null;
        setIsContinuous(false);
        setIsListening(false);
        setIsSpeaking(false);
        setIsTranscribing(false);
        setSpeechStatus('');
    }, [stopFallbackRecording]);

    useEffect(() => {
        if (!voiceReply || !continuousModeRef.current || !waitingReplyRef.current) return;
        waitingReplyRef.current = false;
        const session = sessionRef.current;
        const text = toPlainText(voiceReply.text || '');
        if (!text.trim()) {
            startRecordingRef.current();
            return;
        }
        setIsSpeaking(true);
        setSpeechStatus('Speaking...');
        const controller = new AbortController();
        speechRequestRef.current = controller;
        const speak = async () => {
            try {
                const response = await fetch(`${process.env.REACT_APP_API_URL || '/api/'}Chat/speech`, {
                    method: 'POST', credentials: 'include', signal: controller.signal,
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ text }),
                });
                if (!response.ok) throw new Error(`Speech returned HTTP ${response.status}.`);
                const blob = await response.blob();
                if (!mountedRef.current || session !== sessionRef.current) return;
                const audioUrl = URL.createObjectURL(blob);
                audioUrlRef.current = audioUrl;
                const audio = new Audio(audioUrl);
                audioRef.current = audio;
                audio.onended = () => {
                    URL.revokeObjectURL(audioUrl);
                    if (!mountedRef.current || session !== sessionRef.current) return;
                    audioUrlRef.current = null;
                    audioRef.current = null;
                    setIsSpeaking(false);
                    startRecordingRef.current();
                };
                audio.onerror = () => {
                    if (session !== sessionRef.current) return;
                    stopConversation();
                    setSpeechStatus('Could not play the spoken reply.');
                };
                await audio.play();
            } catch (err) {
                if (!mountedRef.current || session !== sessionRef.current || err.name === 'AbortError') return;
                stopConversation();
                setSpeechStatus(`Voice failed: ${err.message}`);
            }
        };
        speak();
    }, [voiceReply, stopConversation]);

    // Launcher mic: start recording once when the input first mounts
    useEffect(() => {
        if (startListeningOnMount && !disabled) {
            startFallbackRecording();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return (
        <div className="chat-input">
            {speechStatus && (
                <div className="chat-input-status" role="status">
                    {speechStatus}
                </div>
            )}
            <div className="chat-input-row">
                <div className={`chat-input-field${disabled ? ' chat-input-field-disabled' : ''}`}>
                    <textarea
                        ref={textareaRef}
                        className="chat-widget-textarea"
                        value={value}
                        onChange={(e) => onChange(e.target.value)}
                        onKeyDown={handleKeyDown}
                        disabled={disabled}
                        placeholder={placeholder || 'Ask a question'}
                        aria-label={placeholder || 'Ask a question'}
                        rows={1}
                    />
                    {speechSupported && (
                        <div className="chat-input-icons">
                            <button
                                type="button"
                                className={`chat-input-mic${isListening ? ' chat-input-mic-active' : ''}${isTranscribing ? ' chat-input-mic-transcribing' : ''}`}
                                onClick={handleMicToggle}
                                disabled={disabled || isTranscribing || isContinuous}
                                aria-label={isListening ? 'Stop voice input' : 'Start voice input'}
                                title={isTranscribing ? 'Transcribing…' : isListening ? 'Stop voice input' : 'Voice input'}
                            >
                                <ChatIcon src={micIcon} className="chat-input-mic-icon" />
                            </button>
                            <button
                                type="button"
                                className={`chat-input-mic${isContinuous ? ' chat-input-mic-active' : ''}`}
                                onClick={() => {
                                    if (continuousModeRef.current) stopConversation();
                                    else {
                                        continuousModeRef.current = true;
                                        setIsContinuous(true);
                                        startRecordingRef.current();
                                    }
                                }}
                                disabled={!isContinuous && (disabled || isListening || isTranscribing)}
                                aria-label={isContinuous ? 'Stop voice conversation' : 'Start voice conversation'}
                                aria-pressed={isContinuous}
                                title={isContinuous ? 'Stop voice conversation' : 'Voice conversation'}
                            >
                                <ChatIcon src={audioWaveIcon} className={`chat-input-wave${isContinuous ? ' chat-input-wave-active' : ''}`} />
                            </button>
                        </div>
                    )}
                </div>
                <button
                    type="button"
                    className="chat-input-send"
                    onClick={handleSend}
                    disabled={disabled || isListening || isTranscribing || isSpeaking || isContinuous || !value.trim()}
                    aria-label="Send message"
                >
                    <span className="chat-input-send-icon" style={sendMaskStyle} />
                </button>
            </div>
        </div>
    );
}
